/**
 * What a term's state means, and how its dates read (issue #95).
 *
 * Two surfaces need this vocabulary — the registrar's term list and the course
 * catalog — and they must not drift, because the whole point is that an
 * administrator can tell a historical record from a current one at a glance and
 * gets the same answer wherever they look.
 *
 * The pill that renders these lives in `components/records/TermState.tsx`. The
 * split is not decorative: a module exporting both a component and constants
 * breaks Fast Refresh, and these constants are imported by pages that never
 * render the pill.
 */
import type { SemesterStatus } from "@/lib/api/academicStructure";

export const TERM_STATE: Record<
  SemesterStatus,
  { label: string; variant: "success" | "info" | "outline" }
> = {
  active: { label: "Current term", variant: "success" },
  upcoming: { label: "Upcoming", variant: "info" },
  closed: { label: "Closed", variant: "outline" },
};

export const SEMESTER_STATUS_OPTIONS: { value: SemesterStatus; label: string; hint: string }[] = [
  { value: "upcoming", label: "Upcoming", hint: "Planned, not yet running" },
  { value: "active", label: "Current term", hint: "Running now" },
  { value: "closed", label: "Closed", hint: "Over — a historical term" },
];

/** A section in a closed term is historical: recordable, not publishable. */
export function isHistoricalTerm(status: SemesterStatus | null | undefined): boolean {
  return status === "closed";
}

type CourseTermCandidate = {
  semester_status: SemesterStatus | null | undefined;
  semester_start_date?: string | null;
};

/**
 * Choose the course that should open when a deep link did not name one.
 *
 * A current term always beats a planned one. Dated upcoming terms are nearest
 * first, an unassigned course remains usable after planned work, and a closed
 * term is only the final historical fallback.
 */
export function preferredCourse<T extends CourseTermCandidate>(courses: T[]): T | undefined {
  const rank = (course: T): number => {
    if (course.semester_status === "active") return 0;
    if (course.semester_status === "upcoming") return 1;
    if (course.semester_status == null) return 2;
    return 3;
  };
  const dateOrder = (left: T, right: T): number => {
    const leftDate = left.semester_start_date;
    const rightDate = right.semester_start_date;
    if (!leftDate && !rightDate) return 0;
    if (!leftDate) return 1;
    if (!rightDate) return -1;
    if (left.semester_status === "upcoming") return leftDate.localeCompare(rightDate);
    return rightDate.localeCompare(leftDate);
  };

  return [...courses].sort((left, right) => rank(left) - rank(right) || dateOrder(left, right))[0];
}

export function formatTermDate(value: string | null | undefined): string {
  if (!value) return "—";
  // `value` is a plain `YYYY-MM-DD` date, not an instant. `new Date(value)`
  // parses it as UTC midnight and then renders it in local time, which moves a
  // term's start a day earlier anywhere west of Greenwich.
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatTermRange(start: string | null, end: string | null): string {
  if (!start && !end) return "Not dated";
  return `${formatTermDate(start)} – ${formatTermDate(end)}`;
}
